import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { useRememberedState, useRememberedScroll } from '../hooks/useRemembered';
import { Report, UserProfile, ServiceItem, ProjectGlobalData, Contact, GuestParticipant } from '../types';
import { LoadReportDialog } from './LoadReportDialog';
import { ContactsView } from './ContactsView';
import { downloadIcs } from '../services/calendarService';
import { ServiceTrackingModal } from './ServiceTrackingModal';
import { buildPerformerOptions, canChoosePerformer, defaultPerformerId, resolvePerformer } from '../services/performerChoice';
import { useRechte } from '../hooks/useRechte';
import { buchhaltungNurLesen, buchhaltungRecht } from './invoices/rechnungRechte';
import { useGesperrteKonten } from '../hooks/useGesperrteKonten';
import { ohneGesperrte } from '../services/gesperrteKonten';
import { ImportPreviewModal } from './ImportPreviewModal';
import { DataCorrectionModal } from './DataCorrectionModal';
import { ProjectLocationView } from './ProjectLocationView';
import { ProjectLinkingModal } from './ProjectLinkingModal';
import { ProjectSentReportsView } from './project-hub/ProjectSentReportsView';
import { ProjectAccessPanel } from './freelancer/ProjectAccessPanel';

import { ProjectHeader } from './project-hub/ProjectHeader';
import { ProjectModuleGrid } from './project-hub/ProjectModuleGrid';
import { SifaModuleGrid } from './project-hub/SifaModuleGrid';
import { KundenzugangKarte } from './sifa/KundenzugangKarte';
import { useSifaHubData } from './sifa/useSifaHubData';
import { mergeAktivitaet } from '../services/moduleActivity';
import { ProjectStats } from './project-hub/ProjectStats';
import { ProjectMasterData } from './project-hub/ProjectMasterData';
import { ProjectReferenceCard } from './project-hub/ProjectReferenceCard';
import { ProjectLinkBanner } from './project-hub/ProjectLinkBanner';
import { UebungsStreifen } from './project-hub/UebungsStreifen';
import { istPersoenlichesUebungsprojekt, uebungsprojektLoeschen, uebungsprojektZuruecksetzen, vorlageAusNummer } from '../services/uebung/uebungService';
import { ProjectUnlinkModal } from './project-hub/ProjectUnlinkModal';
import { HubOverlay } from './project-hub/HubOverlay';
import { ProjectDataHygieneDialog } from './project-hub/ProjectDataHygieneDialog';
import { findeDatenluecken, brauchtBildPruefung, heuteLokal, istZurueckgestellt, stelleZurueck } from './project-hub/projectDataHygiene';
import { listProjectMedia, MediaListe } from '../services/projectMediaService';
import { zaehleOffeneFreimeldungen } from '../services/freimeldungService';
import { resolveNextInspectionNumber, resolveEffectiveServices, resolveModuleNavTarget, resolveServiceLogsForService, resolveServiceLogSource, resolveBegehungsKnopf } from './project-hub/projectHubLogic';
import { ProjectStandKarte } from './project-hub/ProjectStandKarte';
import { berechneLaufzeit } from './project-hub/projektStand';
import { usePlanungKachel } from './planungsphase/usePlanungKachel';
import { baustartAusGlobalData, lvHatPlanungsleistung } from './planungsphase/planungKachel';
import { useBauordnungKachel } from './bauordnung/useBauordnungKachel';
import { bauordnungKachelAnzeige } from './bauordnung/kachel';

import { Users, ChevronDown, ChevronUp, HardHat } from 'lucide-react';

import { useProjectHubData } from '../hooks/useProjectHubData';
import { useRepresentativeContactSync } from '../hooks/useRepresentativeContactSync';
import { useReportActions } from '../hooks/useReportActions';
import { useProjectLinking } from '../hooks/useProjectLinking';
import { calculateStats } from './project-hub/calculateStats';
import { isInspectionBudgetCountable } from '../services/inspectionBudget';
import { useModuleActivity } from '../hooks/useModuleActivity';
import { useIsMobile } from '../hooks/useIsMobile';
import { MobileBegehungButton } from './project-hub/MobileBegehungButton';
import { appVariante } from '../services/nativePlatform';
import { flatRedirectTitleFor } from '../services/reportBooking';
import { hubModulInAppErlaubt, umfangBeschraenkt, type HubModulKennung } from '../services/appUmfang';
import { BestandteileDialog, type BestandteileKontext } from './bestandteile/BestandteileDialog';
import { modulFuerQuelle, projektIdVon, pruefeSchreiben, setzeOffenUeberschreibung, type OffenUeberschreibung, type StundenFrisch } from './bestandteile/bestandteileDialogLogic';
import { hatBestandteile } from '../services/bestandteile/bestandteilLogic';
import { buildActorName } from '../services/actorName';
import { isAdminRole } from '../services/projectLockLogic';
import type { BestandteilQuelle } from '../types';
import type { ListProject } from '../services/listProject';

import { Wallet as WalletIcon, ArrowRight as ArrowRightIcon } from 'lucide-react';

interface Props {
    report: Report;
    onUpdate: (report: Report) => void;
    onCreateVersion?: (report: Report) => void;
    onNavigate: (module: 'editor' | 'vorank' | 'unterlage' | 'module-sigeko-inspection' | 'project-map' | 'module-archive' | 'module-sent-reports' | 'sigeplan' | 'einweisung' | 'dokumente' | 'planung' | 'sifa-unterweisung' | 'sifa-mitarbeiter') => void;
    onBack: () => void;
    userRole?: string;
    // Sprung in die Buchhaltung (Abrechnung dieses Projekts) — nur mit Recht
    onOpenAccounting?: () => void;
    /** Der volle Speicherstatus der Offline-Queue, nicht nur die vier alten Werte. */
    saveStatus?: import('../hooks/useOfflineSync').SaveStatus;
    currentUser?: UserProfile | null;
    /** Sprung in ein verknuepftes Projekt (useProjectLinking reicht es frisch geladen). */
    onOpenProject?: (project: ListProject) => void;
    /** Sprung in ein Angebot — Nachtragsangebot aus den Stammdaten angelegt. */
    onOpenQuote?: (quoteId: string) => void;
    isReadOnly?: boolean;
    /** Schluessel fuer das Gedaechtnis der Ansicht (Aufgeklapptes, Bildlauf) je Projekt. */
    projectId?: string;
}

export const ProjectHub: React.FC<Props> = ({ report, onUpdate, onCreateVersion, onNavigate, onBack, userRole, onOpenAccounting, currentUser, onOpenProject, onOpenQuote, isReadOnly = false, projectId }) => {
    const memoryKey = `hub.${projectId ?? report.metadata.projectNumber ?? 'x'}`;
    const scrollRef = useRememberedScroll(`${memoryKey}.scroll`);
    const meta = report.metadata;
    // Memoisiert: der Fallback waere sonst bei jedem Render ein neues Objekt
    // und triebe die Datenpflege-Berechnung (useMemo unten) jedes Mal neu an.
    const globalData: ProjectGlobalData = useMemo(() => meta.globalData || {
        projectId: 'temp-id', status: 'planning', budget: 0, budgetUsed: 0,
        notes: [], services: [], serviceLog: [], scope: {}, sections: [], enableSections: false
    }, [meta.globalData]);

    const [activeSectionId] = useState<string | null>(null);
    const [isContactsExpanded, setIsContactsExpanded] = useRememberedState(`${memoryKey}.contacts`, false);
    // Freelancer-Abschnitt (ERP/CRM-Plan Phase D2). Bewusst eingeklappt und
    // erst beim Aufklappen gemountet: das Panel laedt vier Abfragen, die auf
    // Projekten ohne Freelancer niemanden interessieren.
    const [isFreelancerExpanded, setIsFreelancerExpanded] = useRememberedState(`${memoryKey}.freelancer`, false);
    const [, setShowPerformanceModal] = useState(false);
    const [, setShowHistoryModal] = useState(false);
    const [showLocationPicker, setShowLocationPicker] = useState(false);
    useState(false);
    useState<Report | null>(null);
    const projectJsonInputRef = useRef<HTMLInputElement>(null);
    const [showImportModal, setShowImportModal] = useState(false);
    const [pendingImportData] = useState<Report | null>(null);
    const [isPdfImporting] = useState(false);

    // Rollensystem (docs/rollensystem-plan.md §11): Admins haben jedes Recht,
    // ohne geladene Rechte gilt das heutige Verhalten der Systemrolle.
    const rechte = useRechte(userRole ?? currentUser?.role);
    // Preise, Summen, Budget — nur Oberflaeche; der Wert geht unter dem
    // bisherigen Prop-Namen an Stats, Projektstand und die Dialoge.
    const canSeeFinancials = rechte.hat('preise.sehen');
    const isMobile = useIsMobile();
    // Handy-App: nur der Begehungsteil des Hubs (docs/android-app-plan.md
    // 1.4, Vertrag E5/E11). Urteil je Karte in appUmfang; Tablet-App und
    // Browser: immer wahr.
    const variante = appVariante();
    const zeigeKarte = (kennung: HubModulKennung) => hubModulInAppErlaubt(kennung, variante);
    // Die Raster bekommen den Filter nur in der Handy-App — ohne ihn zeichnen
    // sie (Browser, Tablet-App) genau wie bisher.
    const rasterFilter = umfangBeschraenkt(variante) ? zeigeKarte : undefined;
    // SiFa-Dauerprojekt: KEINE Weekly-Vorgabe. Die Betreuung hat keinen
    // Begehungsturnus; die Vorgabe wanderte frueher ueber den Statuswechsel
    // ins JSON und machte das Projekt ab dem Folgetag „ueberfaellig" (D1,
    // docs/sifa-betreuung-plan.md). ProjectStats bekommt hier exakt das,
    // was im JSON steht — meist undefined.
    const istSifa = meta.projectType === 'sifa';
    const intervalConfig = istSifa ? meta.intervalConfig : (meta.intervalConfig || { type: 'weekly' as const });

    // ÜBUNGSPROJEKT (docs/lernzentrum-tutorial-plan.md, 8.1). Erkannt am
    // geladenen Bericht, weil der Hub die DB-Zeile nicht in der Hand hat —
    // `istUebungsprojekt` prueft beide Formen. Die Arbeit liegt in
    // `uebungService`; hier steht nur die Verdrahtung (stehende Entscheidung
    // „duenner Container, Logik in Hooks + pure Funktionen").
    //
    // Der Streifen haengt an `istPersoenlichesUebungsprojekt` und NICHT an
    // `ist_uebung`: die Demo-Projekte der Tutorial-Pipeline (26099001/26099002)
    // tragen dieselbe Spalte, sind aber die Kulisse fuer Video und Screenshot —
    // ein gelber Streifen mit Loeschknopf waere dort auf jedem Bild.
    const istUebung = istPersoenlichesUebungsprojekt({ metadata: meta, project_number: meta.projectNumber });
    const uebungsVorlage = vorlageAusNummer(meta.projectNumber);

    const {
        availableUsers, linkedChildren, linkedParent, setLinkedParent,
        projectEmailLogs, sentReportsLoadFailed, showSentReports, setShowSentReports,
        dbCurrentNr, setDbCurrentNr, dbLastExportedNr, isLegacyCorrected, setIsLegacyCorrected,
        handleOpenSentReports,
    } = useProjectHubData({ globalData, currentUser, isReadOnly });

    // Telefon/E-Mail der Verantwortlichen einmalig gegen die Profile abgleichen:
    // ohne das bleibt die Nummer eines abgelösten Vorgängers am Slot kleben und
    // steht unter dem Namen seines Nachfolgers im Begehungs-PDF.
    useRepresentativeContactSync({ report, availableUsers, onUpdate, isReadOnly });

    const {
        nextDate, setNextDate, nextTime, setNextTime, showLoadDialog, setShowLoadDialog,
        trackingModalState, setTrackingModalState, begruendungMoeglich,
        handleOpenEditorFromGrid, handleContinueDraft, handleReportAction, handleTrackService, handleBookFlatMonth, handleConfirmCorrection,
    } = useReportActions({
        report, onUpdate, onCreateVersion, onNavigate, activeSectionId, dbCurrentNr,
        currentUser, linkedParent, setLinkedParent, setDbCurrentNr, setIsLegacyCorrected, isReadOnly,
    });

    const {
        showLinkingModal, setShowLinkingModal,
        showUnlinkModal, setShowUnlinkModal,
        handleOpenLinkedProject, handleLinkProject, handleChangeBillingMode, handleUnlinkProject,
    } = useProjectLinking({ report, globalData, onUpdate, onOpenProject, isReadOnly });

    const dispatchModuleNav = (module: string) => {
        const target = resolveModuleNavTarget(module);
        if (target.kind === 'location-picker') setShowLocationPicker(true);
        else if (target.kind === 'sent-reports') handleOpenSentReports();
        else onNavigate(target.module as Parameters<Props['onNavigate']>[0]);
    };

    useEffect(() => {
        const handleInternalNav = (e: MessageEvent) => {
            if (e.data?.type === 'NAVIGATE') dispatchModuleNav(e.data.module);
        };
        window.addEventListener('message', handleInternalNav);
        return () => window.removeEventListener('message', handleInternalNav);
    // dispatchModuleNav ist eine je Render neue Funktion; der Empfaenger
    // wird bewusst nur mit onNavigate neu angemeldet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onNavigate]);

    const effectiveServices = resolveEffectiveServices(linkedParent || null, globalData);
    const vorankStats = calculateStats(effectiveServices, 'vorank');
    const unterlageStats = calculateStats(effectiveServices, 'unterlage');
    const sigeplanStats = calculateStats(effectiveServices, 'sigeplan');
    // 'asplan' ist die einzige Zuordnung dieser Liste, die es vor ihrem Modul
    // NICHT schon im LV-Katalog gab (anders als 'planning_general' und
    // 'site_rules'). Der Zaehler bleibt deshalb leer, bis jemand im
    // LV-Editor eine Position auf 'asplan' stellt — der A+S-Plan faellt nur
    // bei kontaminierten Bereichen an und gehoert in kein Standard-LV.
    const asplanStats = calculateStats(effectiveServices, 'asplan');
    const inspectionStats = calculateStats(effectiveServices, 'inspection');
    const einweisungStats = calculateStats(effectiveServices, 'einweisung');
    // 'planning_general' stand im LV-Katalog schon, bevor es das Modul gab —
    // deshalb keine neue ServiceAssignment und keine Anpassung der ~10 Stellen,
    // an denen die Union sonst nachzuziehen waere.
    const planungStats = calculateStats(effectiveServices, 'planning_general');
    // Der Zaehler des Handbuchs ('site_rules', LV-Position „Baustellenordnung
    // erstellen" 2.11.10) stand bis zum Umbau hier. Er haengt seither am
    // Handbuch-REITER in SigePlanModul, weil die Handbuch-Kachel entfallen ist
    // und die SiGe-Plan-Kachel schon ihren eigenen Zaehler traegt.
    // Nur bei Stueckpositionen ist der Nenner der Begehungskachel eine Anzahl.
    const inspectionBudgetCountable = isInspectionBudgetCountable(effectiveServices);
    // „Zuletzt …" unter jeder Kachel: wann im Modul zuletzt etwas geschah.
    const { activity: moduleActivity, reload: reloadModuleActivity } = useModuleActivity(globalData.projectId, report);
    // SiFa-Dauerprojekt: anderes Raster (SifaModuleGrid). Zaehler und
    // Aktivitaet der sifa_*-Tabellen werden NUR dann geladen — der Hook
    // bekommt bei jedem anderen Projekttyp null und fragt nichts ab.
    const sifa = useSifaHubData(istSifa ? globalData.projectId : null);

    // ═══ OFFENE ONLINE-FREIMELDUNGEN ═════════════════════════════════════
    // Nur der ZAEHLER fuer die Begehungskachel (§F). Bewusst nicht der ganze
    // useFreimeldungen-Hook: der Hub braucht weder Links noch Gruppierung, und
    // eine zweite Abfrage je Projektoeffnung waere reine Last. Einmal je
    // Projekt, kein Realtime; bei jedem Fehler 0 — eine Kachel darf nicht
    // daran scheitern, dass Migration 108 noch nicht gelaufen ist.
    // Der Stand traegt das Projekt, fuer das er gilt (Muster useModuleActivity):
    // beim Projektwechsel zeigt die Kachel so nie den Zaehler des vorigen
    // Projekts, ohne dass der Effekt synchron zuruecksetzen muesste.
    const freimeldungProjectId = projectId || globalData.projectId;
    const [freimeldungStand, setFreimeldungStand] = useState<{ fuer: string; offene: number }>({ fuer: '', offene: 0 });
    useEffect(() => {
        if (!freimeldungProjectId || freimeldungProjectId === 'temp-id') return;
        let abgebrochen = false;
        zaehleOffeneFreimeldungen(freimeldungProjectId)
            .then(n => { if (!abgebrochen) setFreimeldungStand({ fuer: freimeldungProjectId, offene: n }); })
            .catch(e => {
                console.warn('Offene Freimeldungen konnten nicht gezählt werden.', e);
                if (!abgebrochen) setFreimeldungStand({ fuer: freimeldungProjectId, offene: 0 });
            });
        return () => { abgebrochen = true; };
    }, [freimeldungProjectId]);
    const offeneFreimeldungen = freimeldungStand.fuer === freimeldungProjectId ? freimeldungStand.offene : 0;

    const handleContactsChange = (c: Contact[]) => onUpdate({ ...report, metadata: { ...report.metadata, distributionList: c } });
    // Kontakte und Gaeste in EINEM Update — zwei Aufrufe hintereinander bauen
    // beide auf demselben `report` auf, der zweite ueberschriebe den ersten.
    const handleParticipantsChange = (c: Contact[], g: GuestParticipant[]) => onUpdate({ ...report, metadata: { ...report.metadata, distributionList: c, guests: g } });

    // ═══ DATENPFLEGE (Betreiber 12.09.2026) ═══════════════════════════════
    // Beim Oeffnen fragen, was an den Stammdaten fehlt — Logik in
    // projectDataHygiene, Oberflaeche in ProjectDataHygieneDialog. Nicht im
    // Lesemodus (nichts wuerde gespeichert) und nicht, solange der
    // Daten-Check (DataCorrectionModal) noch offen ist: zwei Dialoge
    // uebereinander beantwortet niemand. „Später" gilt fuer die Sitzung.
    const hygieneProjectId = projectId || globalData.projectId;
    const heute = heuteLokal();

    // ═══ PLANUNGS-AMPEL (Stufe 1.5/1.6) ══════════════════════════════════
    // Ampel + Klaerungsquote fuer die Planungskachel — aus planungsphasen/
    // planungsfragen gelesen, nichts wird angelegt. Baustart fuer die harten
    // Fristen: Ausfuehrungsbeginn, Rueckfall Projektstart. Die LV-Menge kommt
    // wie der Zaehler aus dem wirksamen LV (bei Toechtern das der Mutter).
    // Nicht bei SiFa (keine Planungsphase) und nicht auf dem Handy, wo die
    // Kachel ausgeblendet ist — die Abfrage entfaellt dann ganz. Die
    // Projektstand-Karte bleibt unberuehrt (Betreiber 25.09.: Laufzeit und
    // Leistungen). Steht hier NACH `heute`: `const` kennt keine Vorgriffe.
    const planungKachel = usePlanungKachel(
        hygieneProjectId,
        { heuteIso: heute, baustartIso: baustartAusGlobalData(globalData), lvHatPlanungsleistung: lvHatPlanungsleistung(planungStats) },
        !istSifa && !isMobile,
    );

    // ═══ BAUSTELLENORDNUNG (Stufe B4) ════════════════════════════════════
    // Eine Statuszeile fuer die Kachel 06 — gelesen wird nur, wo die Kachel
    // steht (dieselbe Bedingung wie im Raster), angelegt wird nichts.
    // Merkmale: der Hub kennt die der Planungsphase nicht (kein Steckbrief),
    // deshalb `{}` — bedingte Kapitel zaehlen dann als „kann sein" mit, die
    // Zahl kann ueber der im Modul liegen.
    const bauordnungAktiv = !istSifa && !isMobile && (!rasterFilter || rasterFilter('bauordnung'));
    const bauordnungLage = useBauordnungKachel(hygieneProjectId, bauordnungAktiv);
    const bauordnungKachel = useMemo(
        () => (bauordnungAktiv ? bauordnungKachelAnzeige(bauordnungLage, {}) : undefined),
        [bauordnungAktiv, bauordnungLage],
    );

    const [hygieneZurueck, setHygieneZurueck] = useState(() => istZurueckgestellt(window.sessionStorage, hygieneProjectId));
    const [hygieneMedia, setHygieneMedia] = useState<MediaListe | null>(null);
    const [mediaRefreshToken, setMediaRefreshToken] = useState(0);
    const bildPruefung = !istSifa && brauchtBildPruefung(globalData, heute);
    // Die Galerie wird NUR abgefragt, wenn das Ende nah genug ist, dass ein
    // Bild Pflicht ist — sonst bleibt der Hub ohne die zusaetzliche Abfrage.
    useEffect(() => {
        if (!bildPruefung || !hygieneProjectId || hygieneZurueck || isReadOnly) return;
        let abgebrochen = false;
        listProjectMedia(hygieneProjectId)
            .then(res => { if (!abgebrochen) setHygieneMedia(res); })
            .catch(() => { if (!abgebrochen) setHygieneMedia({ items: [], tabelleFehlt: true }); });
        return () => { abgebrochen = true; };
    }, [bildPruefung, hygieneProjectId, hygieneZurueck, isReadOnly, mediaRefreshToken]);
    const bildVorhanden: boolean | null = !bildPruefung ? null
        : hygieneMedia === null || hygieneMedia.tabelleFehlt ? null
        : hygieneMedia.items.length > 0;
    const datenLuecken = useMemo(
        () => findeDatenluecken({ metadata: meta, globalData, heute, bildVorhanden }),
        [meta, globalData, heute, bildVorhanden]
    );
    const korrekturOffen = isLegacyCorrected === false && (meta.inspectionNumber || 0) > 1;
    const hygieneDialogOffen = !isReadOnly && !hygieneZurueck && !korrekturOffen
        && datenLuecken.length > 0
        // Mit Bildpflicht erst zeigen, wenn die Galerie geladen ist — sonst
        // spraenge die Bild-Zeile nachtraeglich in den offenen Dialog.
        && (!bildPruefung || hygieneMedia !== null);
    const schliesseHygiene = () => {
        stelleZurueck(window.sessionStorage, hygieneProjectId);
        setHygieneZurueck(true);
    };

    // ═══ PROJEKTSTAND (Betreiber 25.09.2026) ═════════════════════════════
    // Laufzeit und Leistungen als erste Karte — nur lesend, alles aus dem
    // geladenen Stand (docs/projektstand-hub-plan.md). Leistungen und
    // Protokoll beim Tochterprojekt aus der Mutter, wie „Leistung & Budget".
    const standLaufzeit = useMemo(() => berechneLaufzeit(globalData, heute), [globalData, heute]);
    const standLog = useMemo(() => resolveServiceLogSource(globalData, linkedParent || null), [globalData, linkedParent]);
    // Auswahl „Erbracht durch" im Buchungsdialog — nur mit dem Recht
    // „Leistung für andere buchen" gefuellt, sonst leer, und der Dialog zeigt
    // dann kein Feld.
    // Gesperrte Mitglieder (Nr. 148) stehen weder dort noch in der
    // @-Erwähnung der Notizen zur Wahl.
    const { coordinatorId, coordinatorDeputyId, coordinatorDeputy2Id } = meta;
    const gesperrteKonten = useGesperrteKonten();
    const performerOptions = useMemo(
        () => (canChoosePerformer(rechte)
            ? buildPerformerOptions(availableUsers, currentUser, { coordinatorId, coordinatorDeputyId, coordinatorDeputy2Id }, gesperrteKonten)
            : []),
        [rechte, availableUsers, currentUser, coordinatorId, coordinatorDeputyId, coordinatorDeputy2Id, gesperrteKonten],
    );
    const erwaehnbareKollegen = useMemo(() => ohneGesperrte(availableUsers, gesperrteKonten), [availableUsers, gesperrteKonten]);
    const currentUserName = currentUser
        ? ([currentUser.first_name, currentUser.last_name].filter(Boolean).join(' ') || currentUser.email)
        : undefined;

    // ═══ PAKETPOSITIONEN MIT BESTANDTEILEN (docs/positionsbestandteile-plan.md B3) ═══
    // Ein Klick auf eine Paketposition oeffnet den Bestandteile-Dialog statt der
    // Schnellbuchung — Pakete bucht nur der Bestandteile-Weg (Kernregel 7).
    //
    // `holeBericht` liefert den JETZT geltenden Stand: zwischen Klick und
    // Positionsbuchung liegt im Dialog eine Netzrunde (RPC), in der `report`
    // dieses Renders ueberholt sein kann. Die Ref folgt nach jedem Render.
    //
    // GESCHUETZTER SCHREIBWEG: ein Vorgang im Dialog, der fertig wird, nachdem
    // der Hub verlassen oder ein anderes Projekt geoeffnet wurde, darf NIE den
    // Stand des alten Projekts ueber den neuen legen. Geschrieben wird nur,
    // solange der Hub eingehaengt ist und der Stand zum Projekt gehoert, das
    // der Hub gerade zeigt (pruefeSchreiben); sonst verworfen — eine faellige
    // Positionsbuchung holt der Nachtlauf. Der Dialog bindet jeden Vorgang
    // zusaetzlich an das Projekt seines Starts (schuetzeSchreibweg).
    // useLayoutEffect statt useEffect: die Refs stehen damit noch in der
    // Commit-Phase richtig — zwischen Commit und verzoegertem Effekt koennte
    // sonst eine Promise-Fortsetzung den alten Stand fuer aktuell halten.
    const berichtRef = useRef(report);
    useLayoutEffect(() => {
        berichtRef.current = report;
    }, [report]);
    const onUpdateRef = useRef(onUpdate);
    useLayoutEffect(() => {
        onUpdateRef.current = onUpdate;
    });
    const hubEingehaengt = useRef(false);
    useLayoutEffect(() => {
        hubEingehaengt.current = true;
        return () => {
            hubEingehaengt.current = false;
        };
    }, []);
    const holeBericht = useCallback(() => berichtRef.current, []);
    const istHubEingehaengt = useCallback(() => hubEingehaengt.current, []);
    const schreibeGeschuetzt = useCallback((r: Report) => {
        const aktuell = berichtRef.current;
        const sperre = pruefeSchreiben({ eingehaengt: hubEingehaengt.current, projektId: projektIdVon(aktuell), neu: r, aktuell });
        if (sperre) {
            console.warn(`Bestandteile: Schreiben verworfen (${sperre}) — nie in ein anderes Projekt.`);
            return;
        }
        onUpdateRef.current(r);
    }, []);
    const [bestandteileFuer, setBestandteileFuer] = useState<string | null>(null);
    // Die Plakette liest `bestandteileOffen` aus dem geladenen Projekt; nach
    // einer Handlung im Dialog weiss die Datenbank es besser. Statt dafuer zu
    // speichern, haelt der Hub den frischen Wert als Ueberschreibung (nur
    // Anzeige, bestandteileDialogLogic.setzeOffenUeberschreibung).
    const [offenUeberschreibung, setOffenUeberschreibung] = useState<OffenUeberschreibung>({});
    // Mit den frischen Stunden der Position (Stunden-Plakette): erledigen und
    // „Entfällt" melden die neue Summe nicht zurueck, ohne sie stuende bis zum
    // Neuladen des Projekts der alte Stand da.
    const meldeOffen = useCallback(
        (service: ServiceItem, offen: number | null | undefined, unlesbar: boolean, stunden?: StundenFrisch) =>
            setOffenUeberschreibung(vorher => setzeOffenUeberschreibung(vorher, service, offen, unlesbar, stunden)),
        [],
    );
    const rolle = userRole ?? currentUser?.role;
    // Tochter: die Liste zeigt das LV der MUTTER — deren Eintraege, aber nur
    // ansehen (die Automatik bucht in der Tochter nicht).
    const istTochterProjekt = globalData.linkedProjectConfig?.role === 'child';
    const paketProjektId = istTochterProjekt
        ? (linkedParent?.metadata.globalData?.projectId || globalData.linkedProjectConfig?.linkedProjectId || '')
        : (globalData.projectId || projectId || '');
    const bestandteileKontext: BestandteileKontext = {
        projectId: paketProjektId,
        report,
        onUpdate: schreibeGeschuetzt,
        holeBericht,
        istEingehaengt: istHubEingehaengt,
        user: { id: currentUser?.id ?? null, name: buildActorName(currentUser) },
        isAdmin: isAdminRole(rolle),
        isReadOnly: isReadOnly || istTochterProjekt,
        istFreelancer: rolle === 'freelancer',
        serviceLog: standLog,
        // „Zum Modul": dieselbe Navigation wie die Kacheln (Handbuch = Reiter
        // der SiGe-Plan-Huelle, Schluessel 'handbuch').
        onNavigateModule: (quelle: BestandteilQuelle) => {
            const modul = modulFuerQuelle(quelle);
            if (!modul) return;
            setBestandteileFuer(null);
            dispatchModuleNav(modul);
        },
        onOffenGeaendert: meldeOffen,
    };
    const bestandteileService = bestandteileFuer ? effectiveServices.find(s => s.id === bestandteileFuer) : undefined;
    const oeffneBestandteile = (service: ServiceItem) => setBestandteileFuer(service.id);
    // Einstieg aller „Leistung buchen"-Klicks im Hub: Pakete in den Dialog,
    // alles andere wie bisher in die Schnellbuchung.
    const oeffneLeistung = (service: ServiceItem) => {
        if (hatBestandteile(service)) {
            oeffneBestandteile(service);
            return;
        }
        setTrackingModalState({ isOpen: true, service });
    };

    const displayNextNumber = useMemo(
        () => resolveNextInspectionNumber({ activeSectionId, sections: globalData.sections, dbCurrentNr, metaInspectionNumber: meta.inspectionNumber }),
        [activeSectionId, globalData.sections, dbCurrentNr, meta.inspectionNumber]
    );
    // Handy-Begehungsknopf: Aufschrift je Zustand (Entwurf, naechster Bericht,
    // Fremdprojekt, Nur-Ansicht) — dieselben Zaehler wie im Begehungsdialog.
    const begehungsKnopf = resolveBegehungsKnopf({
        reportExempt: globalData.reportExempt === true,
        isReadOnly,
        begruendungMoeglich,
        inspectionNumber: meta.inspectionNumber,
        isExported: meta.isExported,
        dbCurrentNr,
        dbLastExportedNr,
        datum: meta.date,
    });

    return (
        <div ref={scrollRef} data-glass-view="project-hub" className="h-full overflow-y-auto bg-transparent flex flex-col animate-fade-in relative">
            <ProjectHeader metadata={meta} globalData={globalData} activeSectionId={activeSectionId} sections={globalData.sections || []} isPdfImporting={isPdfImporting} onBack={onBack} onDownloadIcs={() => downloadIcs({ id: globalData.projectId, name: meta.projectName, data: report })} onDownloadJson={() => { const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' }); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `${meta.projectName}.json`; a.click(); }} onImportJsonClick={() => projectJsonInputRef.current?.click()} onImportPdfClick={() => {}} />
            {showImportModal && <ImportPreviewModal isOpen={showImportModal} importData={pendingImportData!} existingProject={{ id: globalData.projectId, data: report }} onConfirm={(act, d) => { onUpdate(d); setShowImportModal(false); }} onClose={() => setShowImportModal(false)} />}
            {/* Das Modal hat keinen Abbrechen-Weg: der einzige Knopf schreibt.
                Im Lesemodus wuerde ein bloßes Ansehen eines Altprojekts damit
                unausweichlich die data-Spalte ueberschreiben — es bleibt hier
                deshalb zu. Der Daten-Check holt sich der Nutzer beim regulaeren
                Oeffnen ueber die Projektliste. */}
            <DataCorrectionModal isOpen={!isReadOnly && isLegacyCorrected === false && (meta.inspectionNumber || 0) > 1} projectName={meta.projectName} projectNumber={meta.projectNumber} currentNumber={meta.inspectionNumber || 1} currentDate={meta.date} onConfirm={handleConfirmCorrection} />
            {hygieneDialogOffen && (
                <ProjectDataHygieneDialog
                    luecken={datenLuecken}
                    metadata={meta}
                    globalData={globalData}
                    report={report}
                    projectId={hygieneProjectId}
                    currentUserName={currentUserName}
                    mediaItems={hygieneMedia?.items || []}
                    mediaTabelleFehlt={hygieneMedia?.tabelleFehlt === true}
                    onUpdate={onUpdate}
                    onClose={schliesseHygiene}
                    onMediaChanged={() => setMediaRefreshToken(t => t + 1)}
                />
            )}
            {showLocationPicker && (<HubOverlay widthClass="max-w-5xl" heightClass="h-[85vh]" onClose={() => setShowLocationPicker(false)}><ProjectLocationView report={report} onUpdate={onUpdate} /></HubOverlay>)}
            {showSentReports && (<HubOverlay widthClass="max-w-4xl" heightClass="h-[80vh]" title="Sendeverlauf" zurueckSchliesst onClose={() => setShowSentReports(false)}><ProjectSentReportsView logs={projectEmailLogs} users={availableUsers} contacts={meta.distributionList || []} projectId={globalData.projectId} projectName={meta.projectName} projectNumber={meta.projectNumber} currentUser={currentUser} onResendSuccess={() => { handleOpenSentReports(); reloadModuleActivity(); }} isReadOnly={isReadOnly} loadFailed={sentReportsLoadFailed}/></HubOverlay>)}

            <div className="max-w-7xl mx-auto w-full p-4 md:p-6 space-y-6 md:space-y-8 pb-20">
                {/* Handy: die Begehung steht als erster Knopf im Projekt, noch
                    vor Verknuepfung und Buchhaltung. Die Raster blenden ihre
                    Begehungskachel auf dem Handy dafuer aus (MobileBegehungButton). */}
                {isMobile && zeigeKarte('begehung') && (
                    <MobileBegehungButton
                        zustand={begehungsKnopf}
                        offeneFreimeldungen={offeneFreimeldungen}
                        heute={heute}
                        lastActivity={moduleActivity.begehung}
                        onOpen={handleOpenEditorFromGrid}
                        onFortsetzen={handleContinueDraft}
                    />
                )}
                {/* Mutter/Tochter ist ein Baustellen-Konstrukt (Lose). Bei SiFa
                    nur ausgeblendet, nicht abgebaut (docs/sifa-betreuung-plan.md §1). */}
                {istUebung && zeigeKarte('uebung') && (
                    <UebungsStreifen
                        isReadOnly={isReadOnly}
                        onZuruecksetzen={async () => {
                            if (!uebungsVorlage) {
                                throw new Error('Zu diesem Projekt gehoert keine Uebungsvorlage — es laesst sich nicht zuruecksetzen.');
                            }
                            const { andereId } = await uebungsprojektZuruecksetzen(
                                uebungsVorlage,
                                projectId || globalData.projectId,
                            );
                            // Nur nach Erfolg neu laden, und nur wenn wirklich
                            // DIESES Projekt getroffen wurde: ein Reload nach
                            // einem Treffer daneben brachte den unveraenderten
                            // Stand zurueck und saehe wie „wirkungslos" aus,
                            // waehrend nebenan etwas ueberschrieben wurde.
                            if (andereId) {
                                throw new Error('Zurueckgesetzt wurde ein anderes Uebungsprojekt derselben Vorlage. Dieses Projekt blieb unveraendert — bitte im Uebungsplatz nachsehen.');
                            }
                            // Neu laden statt zu raten: die RPC hat Blob UND
                            // Kindtabellen geschrieben, der Stand im Speicher
                            // ist damit vollstaendig ueberholt.
                            window.location.reload();
                        }}
                        onLoeschen={async () => {
                            const id = projectId || globalData.projectId;
                            if (!id || id === 'temp-id') {
                                throw new Error('Dieses Uebungsprojekt ist noch nicht gespeichert — es gibt nichts zu loeschen.');
                            }
                            await uebungsprojektLoeschen(id);
                            onBack();
                        }}
                    />
                )}
                {/* Projektstand: Laufzeit + Leistungen auf einen Blick. Nicht bei
                    SiFa — die Dauerbetreuung hat kein Ende, ihre Kennzahlen
                    stehen in der Betriebsdaten-Karte. */}
                {!istSifa && zeigeKarte('projektstand') && (
                    <ProjectStandKarte
                        laufzeit={standLaufzeit}
                        services={effectiveServices}
                        log={standLog}
                        heute={heute}
                        canSeeFinancials={canSeeFinancials}
                        mutterName={linkedParent ? linkedParent.metadata.projectName : undefined}
                        isReadOnly={isReadOnly}
                        memoryKey={memoryKey}
                        onTrackService={oeffneLeistung}
                    />
                )}
                {!istSifa && zeigeKarte('verknuepfung') && <ProjectLinkBanner linkedProjectConfig={globalData.linkedProjectConfig} linkedChildrenCount={linkedChildren.length} isReadOnly={isReadOnly} onOpenLinkedProject={handleOpenLinkedProject} onUnlinkProject={() => setShowUnlinkModal(true)} onOpenLinkingModal={() => setShowLinkingModal(true)} onChangeBillingMode={handleChangeBillingMode} />}

                {/* Dieselbe Regel wie der Reiter „Buchhaltung“ der Rechnungen
                    (buchhaltungRecht) — sonst fuehrte die Karte in eine
                    gesperrte Ansicht. Ohne „Rechnungen bearbeiten“ oeffnet
                    sie lesend; das steht dann schon auf der Karte. */}
                {buchhaltungRecht(rechte).erlaubt && onOpenAccounting && zeigeKarte('buchhaltung') && (
                    <button onClick={onOpenAccounting} className="w-full bg-indigo-50 border border-indigo-200 text-indigo-700 rounded-2xl px-6 py-3 flex items-center justify-between hover:bg-indigo-100 transition-colors">
                        <span className="font-bold text-sm flex items-center gap-2"><WalletIcon className="w-4 h-4" /> Buchhaltung dieses Projekts öffnen{buchhaltungNurLesen(rechte) && <span className="text-[11px] font-bold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">Nur lesen</span>}</span>
                        <ArrowRightIcon className="w-4 h-4" />
                    </button>
                )}

                {istSifa ? (
                    <SifaModuleGrid inspectionStats={inspectionStats} onNavigate={dispatchModuleNav} onOpenEditor={handleOpenEditorFromGrid} currentInspectionNumber={displayNextNumber} reportExempt={globalData.reportExempt === true} isReadOnly={isReadOnly} inspectionBudgetCountable={inspectionBudgetCountable} activity={mergeAktivitaet(moduleActivity, sifa.activity)} unterweisungCount={sifa.teilnahmenBestanden} mitarbeiterCount={sifa.mitarbeiter} kennzahlen={sifa.kennzahlen} sollStunden={meta.sifa?.einsatzzeitSollStunden} modulSichtbar={rasterFilter} />
                ) : null}
                {istSifa ? (
                    /* Kundenbereich: Link + PIN fuer den Betrieb (docs/sifa-betreuung-plan.md §4). */
                    zeigeKarte('kundenzugang') ? <KundenzugangKarte projectId={globalData.projectId} isReadOnly={isReadOnly} /> : null
                ) : (
                <ProjectModuleGrid vorankStats={vorankStats} unterlageStats={unterlageStats} sigeplanStats={sigeplanStats} asplanStats={asplanStats} inspectionStats={inspectionStats} einweisungStats={einweisungStats} planungStats={planungStats} planung={planungKachel} bauordnung={bauordnungKachel} onNavigate={dispatchModuleNav} onOpenEditor={handleOpenEditorFromGrid} currentInspectionNumber={displayNextNumber} reportExempt={globalData.reportExempt === true} isReadOnly={isReadOnly} inspectionBudgetCountable={inspectionBudgetCountable} activity={moduleActivity} offeneFreimeldungen={offeneFreimeldungen} modulSichtbar={rasterFilter} />
                )}
                {zeigeKarte('statistik') && <ProjectStats report={report} globalData={globalData} intervalConfig={intervalConfig} sifaKennzahlen={istSifa ? sifa.kennzahlen : undefined} currentUser={currentUser} availableUsers={erwaehnbareKollegen} onUpdate={onUpdate} onShowPerformanceModal={() => setShowPerformanceModal(true)} onShowHistoryModal={() => setShowHistoryModal(true)} onTrackService={oeffneLeistung} onBookFlatMonth={handleBookFlatMonth} canSeeFinancials={canSeeFinancials} linkedParent={linkedParent} isReadOnly={isReadOnly} onOpenBestandteile={oeffneBestandteile} bestandteileKontext={bestandteileKontext} bestandteileOffen={offenUeberschreibung} />}
                {zeigeKarte('stammdaten') && <ProjectMasterData metadata={meta} globalData={globalData} availableUsers={availableUsers} onUpdate={onUpdate} report={report} onShowMap={() => setShowLocationPicker(true)} isReadOnly={isReadOnly} currentUser={currentUser} onOpenQuote={onOpenQuote} />}
                {/* Projektbeschreibung (metadata.projectDescription) + Projektbilder
                    (Tabelle project_media, Nr. 99) fuer Referenzblatt und Social Media
                    (Betreiber 12.09.2026). Eigene Karte im Muster der Stammdaten. */}
                {zeigeKarte('projektbilder') && <ProjectReferenceCard metadata={meta} report={report} projectId={projectId || globalData.projectId} onUpdate={onUpdate} isReadOnly={isReadOnly} memoryKey={memoryKey} refreshToken={mediaRefreshToken} />}

                {zeigeKarte('beteiligte') && <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                    <button onClick={() => setIsContactsExpanded(!isContactsExpanded)} className="w-full text-left px-6 py-4 flex items-center justify-between bg-slate-50/50 hover:bg-slate-100/50" data-tutorial="beteiligte.aufklappen">
                        <div className="flex items-center gap-3"><div className={`p-2 rounded-lg ${isContactsExpanded ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-500'}`}><Users className="w-5 h-5" /></div><h2 className="text-lg font-bold text-slate-800">Projektbeteiligte & Verteiler</h2></div>
                        <div className={`p-2 rounded-full ${isContactsExpanded ? 'bg-slate-100 text-slate-600' : 'text-slate-400'}`}>{isContactsExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}</div>
                    </button>
                    {isContactsExpanded && <div className="p-6 bg-white"><ContactsView contacts={meta.distributionList || []} onChange={handleContactsChange} settings={report.settings} activeSectionId={activeSectionId} sections={globalData.sections} isReadOnly={isReadOnly} guests={meta.guests || []} onParticipantsChange={handleParticipantsChange} /></div>}
                </div>}

                {/* Freelancer-Freischaltung + Begehungsauftraege (ERP/CRM-Plan
                    Phase D2, Workflow Schritte 1–2). Additiv als eigene Karte
                    im Muster der Karte darueber; die Logik steckt vollstaendig
                    in ProjectAccessPanel bzw. den puren Diensten. Nur fuer den
                    Innendienst sichtbar — mit dem Recht „Freelancer verwalten“,
                    das auch die RPCs pruefen (Nr. 153); das hier ist nur die
                    Oberflaeche. */}
                {rechte.hat('freelancer.verwalten') && globalData.projectId && zeigeKarte('freelancer') && (
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                        <button onClick={() => setIsFreelancerExpanded(!isFreelancerExpanded)} className="w-full text-left px-6 py-4 flex items-center justify-between bg-slate-50/50 hover:bg-slate-100/50">
                            <div className="flex items-center gap-3"><div className={`p-2 rounded-lg ${isFreelancerExpanded ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-500'}`}><HardHat className="w-5 h-5" /></div><h2 className="text-lg font-bold text-slate-800">Freelancer & Begehungsaufträge</h2></div>
                            <div className={`p-2 rounded-full ${isFreelancerExpanded ? 'bg-slate-100 text-slate-600' : 'text-slate-400'}`}>{isFreelancerExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}</div>
                        </button>
                        {isFreelancerExpanded && (
                            <div className="p-6 bg-white">
                                <ProjectAccessPanel
                                    projectId={globalData.projectId}
                                    projectName={meta.projectName}
                                    lat={globalData.locationCoordinates?.lat}
                                    lng={globalData.locationCoordinates?.lng}
                                    isReadOnly={isReadOnly}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>

            {showLoadDialog && <LoadReportDialog isOpen={showLoadDialog} projectName={meta.projectName} inspectionNumber={meta.inspectionNumber || 0} dbCurrentNr={dbCurrentNr} dbLastExportedNr={dbLastExportedNr} nextDate={nextDate} nextTime={nextTime} services={(meta.globalData?.services || []) as ServiceItem[]} globalData={meta.globalData} onDateChange={setNextDate} onTimeChange={setNextTime} onDecision={handleReportAction} onClose={() => setShowLoadDialog(false)} activeSectionId={activeSectionId} isExported={meta.isExported} currentProjectNumber={meta.projectNumber} isReadOnly={isReadOnly} />}
            {/* „Erbracht durch" (Recht „Leistung für andere buchen") — die
                Buchhaltung bucht Begehungen fuer Koordinatoren nach; ohne
                Auswahl stuende ihr Name im Nachweis. */}
            {trackingModalState.isOpen && trackingModalState.service && <ServiceTrackingModal isOpen={trackingModalState.isOpen} onClose={() => setTrackingModalState({ isOpen: false, service: null })} service={trackingModalState.service} logs={resolveServiceLogsForService(globalData, linkedParent || null, trackingModalState.service.id)} flatRedirectTitle={flatRedirectTitleFor(effectiveServices, trackingModalState.service.id)} performerOptions={performerOptions} defaultPerformerId={defaultPerformerId(performerOptions, currentUser, meta)} onSave={(amount, description, date, performerId, nepReason) => handleTrackService(trackingModalState.service, amount, description, date, performerId === undefined ? undefined : resolvePerformer(performerId, performerOptions, currentUser), nepReason)} onOpenBestandteile={() => { const s = trackingModalState.service; setTrackingModalState({ isOpen: false, service: null }); if (s) oeffneBestandteile(s); }} />}
            {/* Bestandteile einer Paketposition — Erledigen, Zuruecknehmen,
                Verlauf; die Position bucht der Dialog nur an der ausloesenden
                Handlung (Kernregel 7), nie beim Oeffnen. */}
            {bestandteileService && (
                <BestandteileDialog {...bestandteileKontext} service={bestandteileService} onClose={() => setBestandteileFuer(null)} />
            )}
            {/* Wie der Trennen-Dialog im Lesemodus gar nicht erst gemountet:
                der einzige Knopf des Dialogs schreibt. */}
            {showLinkingModal && !isReadOnly && <ProjectLinkingModal isOpen={showLinkingModal} onClose={() => setShowLinkingModal(false)} currentProject={{ id: globalData.projectId, data: report }} onLink={handleLinkProject} canSeeFinancials={canSeeFinancials} />}
            {/* Der Plan VOR dem Trennen. Im Lesemodus gar nicht erst gemountet:
                der einzige Knopf des Dialogs schreibt. */}
            {showUnlinkModal && !isReadOnly && globalData.linkedProjectConfig?.role === 'child' && globalData.linkedProjectConfig.linkedProjectId && (
                <ProjectUnlinkModal
                    isOpen={showUnlinkModal}
                    onClose={() => setShowUnlinkModal(false)}
                    childProjectId={globalData.projectId}
                    childProjectName={meta.projectName}
                    parentProjectId={globalData.linkedProjectConfig.linkedProjectId}
                    parentProjectName={globalData.linkedProjectConfig.linkedProjectName}
                    canSeeFinancials={canSeeFinancials}
                    onConfirm={handleUnlinkProject}
                />
            )}
        </div>
    );
};
